/**
 * Confirmação de agendamento por WhatsApp — apresentação.
 *
 * Fonte única para rótulo, cor e ícone do estado da confirmação, para os quatro
 * pontos da agenda (bloco na grade, balão de hover, popover de clique e tela de
 * check-in) não divergirem.
 *
 * O backend devolve, em cada agendamento:
 *   confirmation: { status, channel, sentAt, respondedAt, responsePayload } | null
 *
 * `status`: pending | sent | confirmed | cancel_requested | failed
 *
 * ⚠️ Ler SEMPRE aninhado (`appointment.confirmation?.status`). O `status` da raiz
 * do bubble é derivado em ScheduleContext (mistura channel + confirmedByStore) e
 * achatar a confirmação nele causaria colisão.
 */

// Cores alinhadas com a paleta do AgendaInteligente, para as duas telas
// concordarem semanticamente sobre confirmado/cancelado.
//
// ⚠️ `color` TEM de ser hex de 6 dígitos. O alpha é concatenado na string em
// alguns consumidores (`${color}14` para o fundo tingido), o que produz
// #RRGGBBAA. Um hex de 3 dígitos ou uma cor nomeada geraria CSS inválido e o
// fundo simplesmente não apareceria.
export const CONFIRMATION_STATUS = {
  confirmed: {
    code: 'confirmed',
    label: 'Confirmado pelo cliente',
    short: 'Confirmado',
    color: '#10b981',
  },
  cancel_requested: {
    code: 'cancel_requested',
    label: 'Cancelado pelo cliente',
    short: 'Cancelado',
    color: '#ef4444',
  },
  sent: {
    code: 'sent',
    label: 'Aguardando resposta do cliente',
    short: 'Aguardando',
    color: '#909397',
  },
  pending: {
    code: 'pending',
    label: 'Confirmação ainda não enviada',
    short: 'Não enviado',
    color: '#d1d5db',
  },
  failed: {
    code: 'failed',
    label: 'Falha no envio da confirmação',
    short: 'Falha no envio',
    color: '#f59e0b',
  },
}

/**
 * Resolve o estado de apresentação da confirmação.
 * @param {{status?: string}|null} confirmation
 * @returns {object|null} null quando não há confirmação a mostrar
 */
export function getConfirmationState(confirmation) {
  const status = confirmation?.status
  if (!status) return null
  return CONFIRMATION_STATUS[status] || null
}

/**
 * O cliente respondeu? (confirmou ou cancelou)
 * @param {object|null} confirmation
 * @returns {boolean}
 */
export function hasCustomerResponded(confirmation) {
  return ['confirmed', 'cancel_requested'].includes(confirmation?.status)
}

/**
 * Formata data/hora curta em pt-BR para exibir no balão/popover.
 * @param {string|Date|null} value
 * @returns {string}
 */
export function formatConfirmationDate(value) {
  if (!value) return ''
  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) return ''

  return date.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/**
 * Texto completo para o balão: rótulo + quando aconteceu.
 * @param {object|null} confirmation
 * @returns {string}
 */
export function getConfirmationDescription(confirmation) {
  const state = getConfirmationState(confirmation)
  if (!state) return ''

  const when = hasCustomerResponded(confirmation)
    ? formatConfirmationDate(confirmation.respondedAt)
    : formatConfirmationDate(confirmation.sentAt)

  if (!when) return state.label

  const prefix = hasCustomerResponded(confirmation) ? 'em' : 'enviado em'
  return `${state.label} ${prefix} ${when}`
}
